import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Sparkles, Play } from 'lucide-react';
import { semanticsApi, type SemanticEntityType, type SemanticEntityView } from '../../services/semantics-api';
import { handleApiError } from '../../services/api';

/**
 * Read tab for a semantic definition. Renders the live confirmed definition, or
 * — when a proposal is pending — the proposed definition with a review strip
 * (Confirm/Discard reuse the existing version endpoints). Manual editing and
 * "Discuss with AI" land in later phases.
 */
const DefinitionView: React.FC<{
  entity: SemanticEntityView;
  onConfirm: () => void;
  onDiscard: () => void;
  busy: boolean;
}> = ({ entity, onConfirm, onDiscard, busy }) => {
  const pending = !!entity.proposedVersionId;
  const shown = pending ? entity.proposedDefinition : entity.confirmedDefinition;

  return (
    <div className="px-5 py-4 space-y-4">
      {pending && (
        <div className="rounded-[10px] border border-warn/30 bg-warn/[.08] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-lg bg-warn/15 text-warn flex items-center justify-center flex-none">
              <Sparkles className="w-3.5 h-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-foreground">
                {entity.confirmedDefinition ? 'Proposed update awaiting review' : 'Proposed definition awaiting review'}
              </div>
              {entity.proposedNote && (
                <div className="text-[12px] text-muted-foreground truncate">{entity.proposedNote}</div>
              )}
            </div>
            {entity.canConfirm && (
              <div className="flex gap-1.5 flex-none">
                <button
                  disabled={busy}
                  onClick={onConfirm}
                  className="rounded-lg bg-success px-3 py-1 text-[11.5px] font-semibold text-success-foreground hover:opacity-90 disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  disabled={busy}
                  onClick={onDiscard}
                  className="rounded-lg border border-input px-3 py-1 text-[11.5px] font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  Discard
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <SectionLabel>
          {pending ? 'Proposed definition' : 'Definition'}
          {!pending && entity.confirmedVersionNumber != null && (
            <span className="text-muted-foreground font-normal"> · v{entity.confirmedVersionNumber}</span>
          )}
        </SectionLabel>
        <DefinitionBody type={entity.type} definition={shown} />
        <p className="text-[11px] text-muted-foreground mt-2.5">
          Key: <span className="font-mono">{entity.key}</span>
          {entity.sourceName && <> · {entity.sourceName}</>}
          {entity.accountName && entity.accountName !== entity.sourceName && <> · {entity.accountName}</>}
        </p>
        {entity.canManage && entity.type !== 'glossary' && shown && (
          <PreviewRunner entityId={entity.id} definition={shown} />
        )}
      </div>

      {pending && entity.confirmedDefinition && (
        <div className="opacity-70">
          <SectionLabel>Current (live)</SectionLabel>
          <DefinitionBody type={entity.type} definition={entity.confirmedDefinition} />
        </div>
      )}
    </div>
  );
};

// ── Preview (dry-run against the account) ─────────────────────────────────

/** A "Run preview" button that dry-runs a definition and shows the result. */
const PreviewRunner: React.FC<{ entityId: string; definition: Record<string, unknown> }> = ({
  entityId,
  definition,
}) => {
  const preview = useMutation({
    mutationFn: () => semanticsApi.preview(entityId, definition),
  });
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => preview.mutate()}
        disabled={preview.isPending}
        className="inline-flex items-center gap-1.5 rounded-lg border border-input px-2.5 py-1 text-[11.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-50"
      >
        <Play className="w-3 h-3" /> {preview.isPending ? 'Running…' : 'Run preview'}
      </button>
      {preview.isError && (
        <p className="mt-2 text-[11.5px] text-destructive">{handleApiError(preview.error)}</p>
      )}
      {preview.data && (
        <div className="mt-2">
          <PreviewResult result={preview.data} />
        </div>
      )}
    </div>
  );
};

/** Renders the per-type preview payload (value / matches / range / compiled SQL). */
export const PreviewResult: React.FC<{ result: Record<string, unknown> }> = ({ result }) => {
  const warnings = Array.isArray(result.warnings) ? (result.warnings as string[]) : [];
  const rows = Array.isArray(result.rows) ? result.rows : null;
  const compiledSql = typeof result.compiledSql === 'string' ? result.compiledSql : null;
  const note = typeof result.note === 'string' ? result.note : null;
  return (
    <div className="rounded-lg border border-info/30 bg-info/[.06] px-3.5 py-3 space-y-1.5">
      <div className="text-[10px] font-bold uppercase tracking-wider text-info">Preview</div>
      {'value' in result && <Row k="Value" v={<Mono>{fmt(result.value)}</Mono>} />}
      {'matchedRows' in result && (
        <Row k="Matches" v={<Mono>{result.matchedRows === null ? 'n/a' : `${result.matchedRows} rows`}</Mono>} />
      )}
      {('start' in result || 'end' in result) && (
        <Row k="Range" v={<Mono>{fmt(result.start)} → {fmt(result.end)}</Mono>} />
      )}
      {rows && <Row k="Result" v={<Mono>{rows.length} combined rows</Mono>} />}
      {note && <p className="text-[11.5px] text-muted-foreground">{note}</p>}
      {warnings.length > 0 && (
        <ul className="text-[11px] text-warn list-disc pl-4">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
      {compiledSql && (
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
            Compiled SQL
          </div>
          <pre className="font-mono text-[11px] text-foreground whitespace-pre-wrap break-words bg-background rounded p-2 border border-border overflow-x-auto">
            {compiledSql}
          </pre>
        </div>
      )}
    </div>
  );
};

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  return String(v);
}

// ── Per-type readable rendering ───────────────────────────────────────────

export const DefinitionBody: React.FC<{
  type: SemanticEntityType;
  definition: Record<string, unknown> | null | undefined;
}> = ({ type, definition }) => {
  if (!definition) return <Empty>No definition</Empty>;
  switch (type) {
    case 'metric':
      return <MetricBody def={definition} />;
    case 'segment':
      return <SegmentBody def={definition} />;
    case 'glossary':
      return <GlossaryBody def={definition} />;
    case 'time_window':
      return <TimeWindowBody def={definition} />;
    case 'product_mapping':
      return <ProductMappingBody def={definition} />;
    default:
      return <RawJson def={definition} />;
  }
};

const MetricBody: React.FC<{ def: Record<string, unknown> }> = ({ def }) => {
  const grain = typeof def.grain === 'string' ? def.grain : null;
  let body: React.ReactNode;
  if (typeof def.raw_sql === 'string') {
    body = <Code>{def.raw_sql}</Code>;
  } else if (def.kind === 'ratio') {
    body = (
      <div className="space-y-1.5">
        <Row k="Numerator" v={<Mono>{aggText(def.numerator)}</Mono>} />
        <Row k="Denominator" v={<Mono>{aggText(def.denominator)}</Mono>} />
        {def.round !== undefined && <Row k="Round" v={<Mono>{String(def.round)} dp</Mono>} />}
        {def.on_zero_denominator !== undefined && (
          <Row k="If ÷ 0" v={<Mono>{String(def.on_zero_denominator)}</Mono>} />
        )}
      </div>
    );
  } else if (def.kind === 'base') {
    body = <Row k="Aggregate" v={<Mono>{aggText(def)}</Mono>} />;
  } else {
    return <RawJson def={def} />;
  }
  return (
    <Panel>
      {body}
      {grain && <Row k="Grain" v={<Mono>{grain}</Mono>} />}
    </Panel>
  );
};

function aggText(a: unknown): string {
  if (!a || typeof a !== 'object') return '—';
  const o = a as Record<string, unknown>;
  const agg = typeof o.agg === 'string' ? o.agg.toUpperCase() : '?';
  if (agg === 'COUNT' && !o.field) return 'COUNT(*)';
  return `${agg}(${typeof o.field === 'string' ? o.field : ''})`;
}

const SegmentBody: React.FC<{ def: Record<string, unknown> }> = ({ def }) => {
  if (typeof def.raw_sql === 'string') return <Panel><Code>{def.raw_sql}</Code></Panel>;
  if (!def.where) return <RawJson def={def} />;
  return (
    <Panel>
      <SegmentNode node={def.where} depth={0} />
    </Panel>
  );
};

const OP_LABEL: Record<string, string> = {
  equals: '=', not_equals: '≠', in: 'in', not_in: 'not in', ilike: 'like',
  gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between',
  is_null: 'is null', is_not_null: 'is not null',
};

const SegmentNode: React.FC<{ node: unknown; depth: number }> = ({ node, depth }) => {
  if (!node || typeof node !== 'object') return <Mono>—</Mono>;
  const n = node as Record<string, unknown>;
  if (Array.isArray(n.all) || Array.isArray(n.any)) {
    const key = Array.isArray(n.all) ? 'all' : 'any';
    const children = n[key] as unknown[];
    return (
      <div className={clsx(depth > 0 && 'pl-3 border-l border-border ml-1')}>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
          {key === 'all' ? 'ALL of' : 'ANY of'}
        </div>
        <div className="space-y-1">
          {children.map((c, i) => <SegmentNode key={i} node={c} depth={depth + 1} />)}
        </div>
      </div>
    );
  }
  if (n.not) {
    return (
      <div className={clsx(depth > 0 && 'pl-3 border-l border-border ml-1')}>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">NOT</div>
        <SegmentNode node={n.not} depth={depth + 1} />
      </div>
    );
  }
  if (typeof n.segment === 'string') {
    return <div className="text-[12.5px]">segment <Mono>{n.segment}</Mono></div>;
  }
  // leaf
  const op = typeof n.op === 'string' ? (OP_LABEL[n.op] ?? n.op) : '?';
  const val = n.value === undefined ? '' : Array.isArray(n.value) ? n.value.join(', ') : String(n.value);
  return (
    <div className="text-[12.5px] flex items-center gap-1.5 flex-wrap">
      <Mono>{String(n.field ?? '?')}</Mono>
      <span className="text-muted-foreground">{op}</span>
      {val !== '' && <Mono>{val}</Mono>}
    </div>
  );
};

const GlossaryBody: React.FC<{ def: Record<string, unknown> }> = ({ def }) => (
  <Panel>
    <Row k="Means" v={<span className="text-foreground">{String(def.means ?? '—')}</span>} />
    {typeof def.points_to === 'string' && def.points_to && (
      <Row k="Points to" v={<Mono>{def.points_to}</Mono>} />
    )}
  </Panel>
);

const TimeWindowBody: React.FC<{ def: Record<string, unknown> }> = ({ def }) => (
  <Panel>
    <Row k="Start" v={<Mono>{anchorText(def.start)}</Mono>} />
    <Row k="End" v={<Mono>{anchorText(def.end)}</Mono>} />
    {typeof def.align === 'string' && def.align !== 'none' && (
      <Row k="Align" v={<Mono>{def.align}</Mono>} />
    )}
  </Panel>
);

function anchorText(a: unknown): string {
  if (!a || typeof a !== 'object') return '—';
  const o = a as Record<string, unknown>;
  let s = typeof o.anchor === 'string' ? o.anchor : '?';
  if (o.offset && typeof o.offset === 'object') {
    const parts = Object.entries(o.offset as Record<string, unknown>)
      .filter(([, v]) => typeof v === 'number' && v > 0)
      .map(([u, v]) => `${v} ${u}`);
    if (parts.length) s += ` − ${parts.join(', ')}`;
  }
  return s;
}

const ProductMappingBody: React.FC<{ def: Record<string, unknown> }> = ({ def }) => {
  const combine = (def.combine ?? {}) as Record<string, unknown>;
  return (
    <Panel>
      <Row k="Cost" v={<Mono>{sideText(def.cost)}</Mono>} />
      <Row k="Revenue" v={<Mono>{sideText(def.revenue)}</Mono>} />
      <Row k="Combine" v={<Mono>{`${String(combine.as ?? '')} by ${String(combine.by ?? '')}`}</Mono>} />
      {combine.round !== undefined && <Row k="Round" v={<Mono>{String(combine.round)} dp</Mono>} />}
    </Panel>
  );
};

function sideText(s: unknown): string {
  if (!s || typeof s !== 'object') return '—';
  const o = s as Record<string, unknown>;
  const metric = typeof o.metric === 'string' ? o.metric : '?';
  const seg = typeof o.segment === 'string' ? ` where ${o.segment}` : '';
  return `${metric}${seg}`;
}

// ── Small building blocks ─────────────────────────────────────────────────

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-lg border border-border bg-muted/40 px-3.5 py-3 space-y-1.5">{children}</div>
);

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex items-baseline gap-3 text-[12.5px]">
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground w-[92px] flex-none pt-0.5">
      {k}
    </span>
    <span className="min-w-0">{v}</span>
  </div>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-mono text-[12px] text-foreground break-words">{children}</span>
);

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <pre className="font-mono text-[11.5px] text-foreground whitespace-pre-wrap break-words">{children}</pre>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{children}</div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[12.5px] text-muted-foreground italic">{children}</div>
);

const RawJson: React.FC<{ def: Record<string, unknown> }> = ({ def }) => (
  <Panel>
    <pre className="font-mono text-[11px] text-foreground whitespace-pre-wrap break-words">
      {JSON.stringify(def, null, 2)}
    </pre>
  </Panel>
);

export default DefinitionView;
